import React from 'react';
import { motion } from 'framer-motion';
import {
  Building2, Users, FileText, DollarSign, Calendar, BarChart3, Apple, CheckCircle2, ChevronRight,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/ypbcu85o_logo%20inmobiliaria.png';

const T = {
  es: {
    eyebrow: 'Software · InmobiliariaManager',
    heroTitle: 'Toda tu inmobiliaria,',
    heroTitleAccent: 'en un solo lugar.',
    heroSubtitle: 'InmobiliariaManager es la plataforma integral para inmobiliarias en desarrollo: gestiona proyectos, unidades, cotizaciones, reservas, promesas y postventa desde el mismo lugar. Diseñada por inmobiliarios, para inmobiliarios.',
    contactCta: 'Solicitar demo',
    features: 'Módulos',
    featuresTitle: 'Todo lo que tu inmobiliaria necesita.',
    how: 'Cómo funciona',
    howTitle: 'Desde el terreno hasta la escritura.',
    coverage: 'Beneficios',
    coverageTitle: 'Por qué las inmobiliarias eligen IM.',
    trust1: 'En la nube',
    trust2: 'Cifrado',
    trust3: 'Soporte en español',
  },
  en: {
    eyebrow: 'Software · InmobiliariaManager',
    heroTitle: 'Your whole real estate business,',
    heroTitleAccent: 'in one place.',
    heroSubtitle: 'InmobiliariaManager is the end-to-end platform for developing real estate companies: manage projects, units, quotes, reservations, promises and after-sales from a single place. Designed by real estate experts, for real estate experts.',
    contactCta: 'Request a demo',
    features: 'Modules',
    featuresTitle: 'Everything a real estate company needs.',
    how: 'How it works',
    howTitle: 'From land to deed.',
    coverage: 'Benefits',
    coverageTitle: 'Why real estate companies choose IM.',
    trust1: 'Cloud based',
    trust2: 'Encrypted',
    trust3: 'Spanish support',
  },
};

const InmobiliariaManager = () => {
  const { lang } = useLanguage();
  const s = T[lang] || T.es;

  const features = [
    { icon: Building2, t: lang === 'en' ? 'Projects and units' : 'Proyectos y unidades', d: lang === 'en' ? 'Manage every building, tower, floor and apartment with full technical detail.' : 'Gestiona cada edificio, torre, piso y departamento con detalle técnico completo.' },
    { icon: FileText, t: lang === 'en' ? 'Quotes and reservations' : 'Cotizaciones y reservas', d: lang === 'en' ? 'Generate quotes, block units and track the sales pipeline in real time.' : 'Genera cotizaciones, bloquea unidades y sigue el pipeline en tiempo real.' },
    { icon: DollarSign, t: lang === 'en' ? 'Payment schedules' : 'Cronogramas de pago', d: lang === 'en' ? 'Automatic payment plans, reminders and reconciliation for every buyer.' : 'Planes de pago automáticos, recordatorios y conciliación por cada comprador.' },
    { icon: Users, t: lang === 'en' ? 'CRM built-in' : 'CRM incorporado', d: lang === 'en' ? 'Every lead, tour and conversation logged in the client\u2019s timeline.' : 'Cada prospecto, visita y conversación registrada en la línea de tiempo del cliente.' },
    { icon: Calendar, t: lang === 'en' ? 'After-sales tickets' : 'Tickets de postventa', d: lang === 'en' ? 'Turn every buyer complaint into a tracked ticket with SLA and assignment.' : 'Cada observación de postventa se transforma en un ticket con SLA y responsable.' },
    { icon: BarChart3, t: lang === 'en' ? 'Real-time dashboards' : 'Dashboards en tiempo real', d: lang === 'en' ? 'Sales, cashflow, absorption and project health at a glance.' : 'Ventas, cashflow, absorción y salud de cada proyecto a la vista.' },
  ];

  const benefits = lang === 'en' ? [
    'Cut sales admin work by 70% with automated documents',
    'Never lose a lead again: full CRM with WhatsApp and email tracking',
    'Real-time cashflow view aligned to signed promises and unit deliveries',
    'One-click reports for the board, banks and financial partners',
    'Ready for multi-project management from your first sale',
    'Integrations with Chilean SII, DTE and bank reconciliation',
  ] : [
    'Reduce el trabajo administrativo de ventas en un 70% con documentos automáticos',
    'Nunca pierdas un lead: CRM completo con seguimiento WhatsApp y email',
    'Cashflow en tiempo real alineado a promesas firmadas y entregas',
    'Reportes de un click para directorio, bancos y partners financieros',
    'Listo para gestionar multi-proyecto desde tu primera venta',
    'Integraciones con SII, DTE chileno y conciliación bancaria',
  ];

  const steps = lang === 'en' ? [
    { n: '01', t: 'Configure your project', d: 'Load the master plan, unit sheet, plans and typical prices.' },
    { n: '02', t: 'Sell and reserve', d: 'The sales team quotes, blocks and reserves units in seconds.' },
    { n: '03', t: 'Deliver and follow up', d: 'From promise to deed, and from delivery to after-sales — end to end.' },
  ] : [
    { n: '01', t: 'Configuras tu proyecto', d: 'Cargas plan maestro, ficha de unidades, planos y precios tipo.' },
    { n: '02', t: 'Vendes y reservas', d: 'El equipo cotiza, bloquea y reserva unidades en segundos.' },
    { n: '03', t: 'Entregas y postventa', d: 'De la promesa a la escritura, y de la entrega al servicio postventa — end to end.' },
  ];

  return (
    <div data-testid="im-marketing">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: '/que-hacemos/desarrollo-software', label: 'Software' },
        { to: null, label: 'InmobiliariaManager' },
      ]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 light-beam" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12 pt-4 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeIn><Eyebrow>{s.eyebrow}</Eyebrow></FadeIn>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
                className="mt-8 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
              >
                {s.heroTitle}
                <br />
                <span className="text-amber-300">{s.heroTitleAccent}</span>
              </motion.h1>
              <FadeIn delay={0.25}>
                <p className="mt-8 max-w-xl font-body text-base sm:text-lg text-white/65 leading-relaxed">{s.heroSubtitle}</p>
              </FadeIn>
              <FadeIn delay={0.35}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="mailto:info@tair.site" data-testid="im-demo-cta" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-amber-300 text-[#0a1322] font-semibold text-sm hover:bg-amber-200 transition-colors">{s.contactCta}</a>
                  <a href="mailto:info@tair.site" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-transparent border border-white/20 text-white/85 font-medium text-sm hover:bg-white/5 hover:border-white/40 transition-colors">info@tair.site</a>
                </div>
              </FadeIn>
              <FadeIn delay={0.5}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust1}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust2}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust3}</span>
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={0.35}>
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-sky-400/20 rounded-full" />
                  <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 bg-[#0a1322]">
                    <img src={LOGO} alt="InmobiliariaManager" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-widest text-white/40 bg-[#070d18]/80 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur">SaaS · Web</div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-24">
        <FadeIn><Eyebrow>{s.features}</Eyebrow></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.featuresTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                  <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300"><Icon className="w-5 h-5" /></div>
                  <h4 className="mt-6 font-display font-bold text-lg md:text-xl text-white tracking-tight">{f.t}</h4>
                  <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{f.d}</p>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      <Section className="pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-5">
            <Eyebrow>{s.coverage}</Eyebrow>
            <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl text-white leading-[1.1]">{s.coverageTitle}</h2>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-7">
            <ul className="space-y-3">
              {benefits.map((c, i) => (
                <li key={i} className="flex items-start gap-4 rounded-xl border border-white/10 bg-white/[0.02] p-5 card-hover">
                  <ChevronRight className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
                  <span className="text-white/80 font-body">{c}</span>
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-32">
        <FadeIn><Eyebrow>{s.how}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}>
          <h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.howTitle}</h2>
        </FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">{step.n}</div>
                <h4 className="mt-4 font-display font-bold text-xl text-white tracking-tight">{step.t}</h4>
                <p className="mt-3 font-body text-white/60 leading-relaxed">{step.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>
    </div>
  );
};

export default InmobiliariaManager;
